/**
 * Les machines du parc qui ont la marque de la gamme et l'un de ses modèles — et le rattachement en
 * lot de celles qui n'ont pas encore de gamme.
 *
 * Rattacher reste EXPLICITE (CDC §8.3) : on coche, on confirme. Sont cochées d'emblée les seules
 * machines sans doute : celles dont la référence constructeur est bien celle de la gamme, ou dont
 * la gamme ne dépend d'aucune variante. Une machine portant un avertissement (autre variante,
 * référence non renseignée) reste décochée ; la cocher est possible, avec une confirmation.
 *
 * Il n'existe pas de route groupée : on rattache machine par machine, et un refus n'arrête pas les
 * autres — le bilan dit ce qui a réussi et ce qui a été refusé, et pourquoi.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type GammeDetail } from '../api/client';
import { decrireAvertissementReference } from '../domaine/gammes';
import {
  avertissementCourt,
  machinesCocheesParDefaut,
  machineRattachable,
  type ResultatRattachement,
} from '../domaine/gammes-versions';

export function MachinesCorrespondantes({
  detail,
  peutGerer,
  enCours,
  onRattachees,
}: {
  detail: GammeDetail;
  peutGerer: boolean;
  enCours: boolean;
  /** Reçoit le résultat de chaque machine, dans l'ordre ; l'écran en fait le bilan et relit la gamme. */
  onRattachees: (resultats: ResultatRattachement[]) => void;
}) {
  const [coches, setCoches] = useState<ReadonlySet<string>>(() => new Set(machinesCocheesParDefaut(detail)));
  const [travail, setTravail] = useState(false);

  // La liste se relit après chaque geste : les cases repartent de leur état par défaut.
  useEffect(() => {
    setCoches(new Set(machinesCocheesParDefaut(detail)));
  }, [detail]);

  const machines = detail.machinesCorrespondantes;
  const rattachables = machines.filter((m) => machineRattachable(detail, m));
  const aRattacher = rattachables.filter((m) => coches.has(m.id));
  const avertis = aRattacher.filter((m) => m.avertissementReference !== null);

  const basculer = (id: string) =>
    setCoches((actuelles) => {
      const suivantes = new Set(actuelles);
      if (suivantes.has(id)) suivantes.delete(id);
      else suivantes.add(id);
      return suivantes;
    });

  const rattacher = async () => {
    if (aRattacher.length === 0) return;
    if (avertis.length > 0) {
      const confirme = window.confirm(
        `${avertis.length} machine${avertis.length > 1 ? 's' : ''} cochée${avertis.length > 1 ? 's' : ''} ` +
          `porte${avertis.length > 1 ? 'nt' : ''} un avertissement de référence (autre variante, ou référence non renseignée) : ` +
          'les pièces de cette gamme peuvent ne pas lui convenir.\n\nRattacher quand même ?',
      );
      if (!confirme) return;
    }

    setTravail(true);
    const resultats: ResultatRattachement[] = [];
    for (const m of aRattacher) {
      const nom = m.label ?? m.model ?? m.id;
      try {
        await api.assignMaintenance(m.id, detail.id);
        resultats.push({ machine: nom, erreur: null });
      } catch (e) {
        resultats.push({
          machine: nom,
          erreur: e instanceof ApiError || e instanceof Error ? e.message : 'refusée',
        });
      }
    }
    setTravail(false);
    onRattachees(resultats);
  };

  const occupe = enCours || travail;

  return (
    <div className="carte">
      <div className="entete-carte">
        <h2>Machines du parc correspondantes</h2>
        {peutGerer && rattachables.length > 0 && (
          <div className="saisie-ligne">
            <button
              className="secondaire discret"
              disabled={occupe}
              onClick={() => setCoches(new Set(machinesCocheesParDefaut(detail)))}
            >
              Sans avertissement
            </button>
            <button className="secondaire discret" disabled={occupe} onClick={() => setCoches(new Set())}>
              Aucune
            </button>
            <button disabled={occupe || aRattacher.length === 0} onClick={() => void rattacher()}>
              {travail
                ? 'Rattachement…'
                : `Rattacher ${aRattacher.length} machine${aRattacher.length > 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
      <div className="carte-corps">
        {machines.length === 0 ? (
          <div className="vide">Aucune machine du parc n’a cette marque et l’un de ces modèles.</div>
        ) : (
          <>
            {peutGerer && detail.archivedAt === null && detail.enVigueur === null && (
              <p className="aide">
                Cette gamme n’a pas de version en vigueur : publiez-en une avant d’y rattacher des machines.
              </p>
            )}
            <table>
              <thead>
                <tr>
                  {peutGerer && <th style={{ width: 36 }} aria-label="Rattacher" />}
                  <th>Machine</th>
                  <th>Modèle</th>
                  <th>N° de série</th>
                  <th>Site</th>
                  <th>Gamme rattachée</th>
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => {
                  const rattachable = machineRattachable(detail, m);
                  const court = avertissementCourt(m);
                  return (
                    <tr key={m.id}>
                      {peutGerer && (
                        <td>
                          {rattachable && (
                            <input
                              type="checkbox"
                              aria-label={`Rattacher ${m.label ?? m.model ?? m.id}`}
                              checked={coches.has(m.id)}
                              disabled={occupe}
                              onChange={() => basculer(m.id)}
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <Link to={`/parc?asset=${m.id}`}>{m.label ?? m.model ?? m.id}</Link>
                      </td>
                      <td>{m.model ?? '—'}</td>
                      <td>{m.serialNumber ?? '—'}</td>
                      <td>{m.siteName ?? '—'}</td>
                      <td>
                        {m.rattacheeA ? (
                          <code>{m.rattacheeA}</code>
                        ) : court && m.avertissementReference ? (
                          <span
                            className="etat attente"
                            title={decrireAvertissementReference(m.avertissementReference)}
                          >
                            {court}
                          </span>
                        ) : (
                          <span className="aide">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
